import {
  Landmark,
  Building2,
  Briefcase,
  Wallet,
  BadgeDollarSign,
  Globe,
} from "lucide-react";

import ServiceCard from "./ServiceCard";

const services = [
  {
    number: "01",
    icon: <Landmark size={18} />,
    title: "Banking & Institutional Lending",
    description:
      "Structuring debt through commercial, public sector, private, foreign, and small finance banks, as well as Development Financial Institutions.",
    tags: [
      "Working Capital",
      "Term Loans",
      "Trade Finance",
      "Syndicated Loans",
    ],
  },
  {
    number: "02",
    icon: <Building2 size={18} />,
    title: "NBFCs & Housing Finance",
    description:
      "Flexible financing through NBFCs, Housing Finance Companies, Infrastructure Finance Companies, and specialized lenders for complex situations.",
    tags: ["LAP", "Construction Finance", "Bridge Finance", "Promoter Funding"],
  },
  {
    number: "03",
    icon: <Briefcase size={18} />,
    title: "Private Credit & Alternative Lenders",
    description:
      "Customized financing from credit funds, family offices, and HNIs for special situations, venture debt, and revenue-based structures.",
    tags: ["Mezzanine", "Rescue Finance", "RBF", "Structured Debt"],
  },
  {
    number: "04",
    icon: <Wallet size={18} />,
    title: "AIF, Credit Funds & Institutional Capital",
    description:
      "Accessing institutional capital through AIFs, credit funds, real estate funds, infrastructure debt funds, and co-investment platforms.",
    tags: ["Private Debt", "Real Estate Credit", "Capital Recycling"],
  },
  {
    number: "05",
    icon: <BadgeDollarSign size={18} />,
    title: "Equity & Private Capital",
    description:
      "Connecting businesses with investors at every growth stage — from angel and seed through venture, private equity, and impact capital.",
    tags: ["Angel/VC", "Private Equity", "Pre-IPO", "SWFs"],
  },
  {
    number: "06",
    icon: <Globe size={18} />,
    title: "Capital Markets & Public Investors",
    description:
      "Raising equity and debt through listed securities — IPO, SME IPO, QIP, NCDs, REITs, InvITs, and listing advisory services.",
    tags: ["IPO / SME IPO", "NCD / Bonds", "REITs", "QIP"],
  },
  {
    number: "07",
    icon: <Globe size={18} />,
    title: "Hybrid & Structured Instruments",
    description:
      "Designing innovative financing structures combining debt, equity, and convertibles — CCDs, OCDs, SAFEs, preference shares, and warrants.",
    tags: ["CCDs / OCDs", "SAFE Notes", "Quasi-Equity"],
  },
  {
    number: "08",
    icon: <Globe size={18} />,
    title: "Government & Development Finance",
    description:
      "Facilitating access to government-backed finance, CGTMSE, SIDBI, Mudra, PLI, subsidy advisory, and PPP funding structures.",
    tags: ["CGTMSE", "SIDBI", "NABARD", "PLI"],
  },
  {
    number: "09",
    icon: <Globe size={18} />,
    title: "International & Cross-Border Capital",
    description:
      "Connecting businesses with global lenders, ECBs, FDI, export credit agencies, multilateral banks, and offshore debt markets.",
    tags: ["ECBs", "FDI / FPI", "ECAs", "MDBs"],
  },
  {
    number: "10",
    icon: <Globe size={18} />,
    title: "Debt Restructuring & Refinancing",
    description:
      "Helping businesses renegotiate, consolidate, or refinance existing debt to reduce cost of capital, extend tenors, and improve balance sheet health.",
    tags: [
      "OTS / Settlement",
      "Refinancing",
      "Debt Consolidation",
      "NPA Resolution",
    ],
  },
  {
    number: "11",
    icon: <Globe size={18} />,
    title: "Project Finance & Infrastructure Funding",
    description:
      "Structuring long-tenor project finance for infrastructure, energy, and industrial projects — including SPV-based funding, concession finance, and DFI capital.",
    tags: [
      "SPV Finance",
      "EPC Funding",
      "DFI / Multilaterals",
      "Concession Finance",
    ],
  },
  {
    number: "12",
    icon: <Globe size={18} />,
    title: "MSME & Supply Chain Finance",
    description:
      "Tailored financing solutions for MSMEs, including anchor-led supply chain programs, vendor finance, and government-backed MSME credit schemes.",
    tags: ["Vendor Finance", "Dealer Finance", "CGTMSE", "Mudra / SIDBI"],
  },
  {
    number: "13",
    icon: <Globe size={18} />,
    title: "Real Estate Finance",
    description:
      "End-to-end capital solutions for real estate developers and investors — from construction finance and LRD to plotted development funding and land acquisition loans.",
    tags: ["Construction Finance", "LRD", "LAP", "Land Acquisition"],
  },
  {
    number: "14",
    icon: <Globe size={18} />,
    title: "Mergers & Acquisitions (M&A) Advisory",
    description:
      "Supporting buy-side and sell-side M&A transactions with financial structuring, valuation, due diligence coordination, and deal financing advisory.",
    tags: [
      "Buy-Side Advisory",
      "Sell-Side Advisory",
      "Acquisition Finance",
      "Valuation",
    ],
  },
  {
    number: "15",
    icon: <Globe size={18} />,
    title: "Working Capital Optimisation",
    description:
      "Designing efficient working capital structures — CC limits, OD facilities, bill discounting, and cash credit — to maximise liquidity and minimise idle capital.",
    tags: [
      "CC / OD Limits",
      "Bill Discounting",
      "Packing Credit",
      "Cash Management",
    ],
  },
  {
    number: "16",
    icon: <Globe size={18} />,
    title: "Invoice Discounting & Factoring",
    description:
      "Unlocking working capital tied in receivables through invoice discounting, trade receivables financing, and TReDS platform facilitation for faster cash conversion.",
    tags: ["Invoice Discounting", "Factoring", "TReDS", "Receivables Finance"],
  },
  {
    number: "17",
    icon: <Globe size={18} />,
    title: "Startup & Early-Stage Funding",
    description:
      "Guiding startups through their funding journey — from pre-seed and seed rounds to Series A and beyond — with pitch preparation, investor mapping, and term sheet advisory.",
    tags: ["Pre-Seed / Seed", "Angel Networks", "Series A–C", "Pitch Advisory"],
  },
];
export default function ServiceGrid() {
  return (
    <section className="py-20" id="services">
      <div className="mx-auto max-w-7xl px-6">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[4px] text-[#d9bc6d]">
          Capital Providers & Funding Solutions
        </p>

        <h2 className="max-w-3xl font-serif text-4xl leading-tight text-[#13254d] md:text-6xl">
          One advisor.
          <br />
          Every source of capital.
        </h2>

        <p className="mt-6 max-w-2xl text-gray-500 leading-8">
          We structure, arrange, and syndicate capital across the full spectrum
          — debt, equity, hybrid instruments, and government-backed schemes.
        </p>

        <div className="mt-20 grid gap-0 border border-[#ececec] md:grid-cols-2 xl:grid-cols-3">
          {services.map((service) => (
            <ServiceCard key={service.number} {...service} />
          ))}
        </div>
      </div>
    </section>
  );
}
